import { LinkingOptions, NavigationContainer, PathConfigMap } from '@react-navigation/native';
import React, { useEffect, useRef } from 'react';
import { currentRoute, navigationRef } from './RootNavigation';
import { useAppDispatch, useAppSelector } from './store/hooks';
import {
  ACCESS_TOKEN,
  authSlice,
  REFRESH_TOKEN,
  selectIsAuthenticated,
} from './store/services/auth';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { createSidebarNavigator } from './SidebarNavigator';
import { RootStackParamList } from './types';
import {
  AccountCodeScreen,
  CatalogScreen,
  CatalogShopScreen,
  CatalogSnapshotScreen,
  CustomerExportScreen,
  CustomerScreen,
  DeliveryZoneScreen,
  FiscalScreen,
  ForgotPassword,
  IngredientScreen,
  RuptureScreen,
  Login,
  NotFound,
  OptionScreen,
  OrderSearchScreen,
  PackScreen,
  PaymentMethodScreen,
  PaymentProviderScreen,
  ProductScreen,
  PromotionScreen,
  ResetPassword,
  RoleScreen,
  ServiceScreen,
  ShopScreen,
  StepScreen,
  TagScreen,
  TaxScreen,
  TimesheetScreen,
  VariationScreen,
} from './screens';
import { ToasterContainer } from './components/toasters/ToasterContainer';
import { useTriptych } from './hooks';
import { PublicityScreen } from './screens/PublicityScreen';
import { OrderCancelReasonScreen } from './screens/OrderCancelReasonScreen';
import { ConnectorScreen } from './screens/ConnectorScreen';
import { KitchenAreaScreen } from './screens/KitchenAreaScreen';
import { OrganizationScreen } from './screens/OrganizationScreen';
import { LoyaltyScreen } from './screens/LoyaltyScreen';
import { UserScreen } from './screens/UserScreen';
import { Permission } from '@tickeat/permissions';
import { useCheckPermissions } from './hooks/useCheckPermissions';

export const ROUTES: {
  name: keyof RootStackParamList;
  path: string;
  component: React.ComponentType<any>;
  options: any;
  permissions: Permission[];
}[] = [
  {
    name: 'ProductScreen',
    path: 'produits',
    component: ProductScreen,
    options: { title: 'Produits' },
    permissions: ['dashboard::products.read'],
  },
  {
    name: 'AccountCodeScreen',
    path: 'codes-comptables',
    component: AccountCodeScreen,
    options: { title: 'Codes comptables' },
    permissions: ['dashboard::account-codes.read'],
  },
  {
    name: 'VariationScreen',
    path: 'variations',
    component: VariationScreen,
    options: { title: 'Variations' },
    permissions: ['dashboard::variations.read'],
  },
  {
    name: 'PackScreen',
    path: 'menus',
    component: PackScreen,
    options: { title: 'Menus' },
    permissions: ['dashboard::packs.read'],
  },
  {
    name: 'StepScreen',
    path: 'etapes',
    component: StepScreen,
    options: { title: 'Etapes' },
    permissions: ['dashboard::steps.read'],
  },
  {
    name: 'IngredientScreen',
    path: 'ingredients',
    component: IngredientScreen,
    options: { title: 'Ingredients' },
    permissions: ['dashboard::ingredients.read'],
  },
  {
    // Maxence, 1er nov. 2026 : les ruptures d'ingrédients du restaurant
    // sélectionné, comme sur la caisse ; permission dédiée.
    name: 'RuptureScreen',
    path: 'ruptures',
    component: RuptureScreen,
    options: { title: 'Ruptures' },
    permissions: ['dashboard::ingredients.out-of-stock'],
  },
  {
    name: 'OptionScreen',
    path: 'options',
    component: OptionScreen,
    options: { title: 'Options' },
    permissions: ['dashboard::options.read'],
  },
  {
    name: 'CatalogScreen',
    path: 'catalogues',
    component: CatalogScreen,
    options: { title: 'Liste des catalogues' },
    permissions: ['dashboard::catalogs.read'],
  },
  {
    name: 'CatalogSnapshotScreen',
    path: 'historique-publications',
    component: CatalogSnapshotScreen,
    options: { title: 'Historique des publications' },
    permissions: ['dashboard::catalog-snapshots.read'],
  },
  {
    name: 'CatalogShopScreen',
    path: 'prix-restaurant',
    component: CatalogShopScreen,
    options: { title: 'Prix du restaurant' },
    permissions: ['dashboard::catalog-snapshot-to-shops.read'],
  },
  {
    name: 'TagScreen',
    path: 'categories',
    component: TagScreen,
    options: { title: 'Catégories' },
    permissions: ['dashboard::tags.read'],
  },
  {
    name: 'KitchenAreaScreen',
    path: 'lieu-de-fabrication',
    component: KitchenAreaScreen,
    options: { title: 'Lieux de fabrication' },
    permissions: ['dashboard::kitchen-areas.read'],
  },
  {
    name: 'ShopScreen',
    path: 'restaurants',
    component: ShopScreen,
    options: { title: 'Restaurants' },
    permissions: [],
  },
  {
    name: 'PromotionScreen',
    path: 'promotions',
    component: PromotionScreen,
    options: { title: 'Promotions' },
    permissions: ['dashboard::promotions.read'],
  },
  {
    name: 'PaymentMethodScreen',
    path: 'moyens-de-paiement',
    component: PaymentMethodScreen,
    options: { title: 'Moyens de paiement' },
    permissions: ['dashboard::payment-methods.read'],
  },
  {
    name: 'PublicityScreen',
    path: 'publicites',
    component: PublicityScreen,
    options: { title: 'Publicités' },
    permissions: ['dashboard::advertisements.read'],
  },
  {
    name: 'OrderCancelReasonScreen',
    path: 'raisons-annulations',
    component: OrderCancelReasonScreen,
    options: { title: "Raisons d'annulations" },
    permissions: ['dashboard::order-cancel-reasons.read'],
  },
  {
    name: 'CustomerScreen',
    path: 'clients',
    component: CustomerScreen,
    options: { title: 'Clients' },
    permissions: ['dashboard::customers.read'],
  },
  {
    name: 'CustomerExportScreen',
    path: 'clients/export',
    component: CustomerExportScreen,
    options: { title: 'Export Clients' },
    permissions: ['dashboard::customers.export-csv'],
  },
  {
    name: 'ConnectorScreen',
    path: 'connecteurs',
    component: ConnectorScreen,
    options: { title: 'Connecteurs' },
    permissions: ['dashboard::connectors.read'],
  },
  {
    name: 'OrganizationScreen',
    path: 'organisation',
    component: OrganizationScreen,
    options: { title: 'Organisation' },
    permissions: ['dashboard::organizations.read'],
  },
  {
    name: 'LoyaltyScreen',
    path: 'fidelite',
    component: LoyaltyScreen,
    options: { title: 'Programmes de fidélité' },
    permissions: ['dashboard::loyalties.read'],
  },
  {
    name: 'ServiceScreen',
    path: 'services',
    component: ServiceScreen,
    options: { title: 'Services' },
    permissions: ['dashboard::services.read'],
  },
  {
    name: 'OrderSearchScreen',
    path: 'recherche-commandes',
    component: OrderSearchScreen,
    options: { title: 'Recherche de commandes' },
    permissions: ['dashboard::services.read'],
  },
  {
    name: 'FiscalScreen',
    path: 'conformite-nf525',
    component: FiscalScreen,
    options: { title: 'Conformité fiscale' },
    permissions: ['dashboard::fiscal.read'],
  },
  {
    name: 'PaymentProviderScreen',
    path: 'prestataires-paiement',
    component: PaymentProviderScreen,
    options: { title: 'Prestataires de paiement' },
    permissions: ['dashboard::payment-providers.read'],
  },
  {
    name: 'TaxScreen',
    path: 'taxes',
    component: TaxScreen,
    options: { title: 'Taxes' },
    permissions: ['dashboard::taxes.read'],
  },
  {
    name: 'UserScreen',
    path: 'utilisateurs',
    component: UserScreen,
    options: { title: 'Utilisateurs' },
    permissions: ['dashboard::users.read'],
  },
  {
    name: 'RoleScreen',
    path: 'roles',
    component: RoleScreen,
    options: { title: 'Rôles' },
    permissions: ['dashboard::roles.read'],
  },
  {
    name: 'DeliveryZoneScreen',
    path: 'zones-de-livraison',
    component: DeliveryZoneScreen,
    options: { title: 'Zones de livraison' },
    permissions: ['dashboard::delivery-zones.read'],
  },
  {
    name: 'TimesheetScreen',
    path: 'pointages',
    component: TimesheetScreen,
    options: { title: 'Pointages' },
    permissions: ['dashboard::timesheets.read'],
  },
  {
    name: 'NotFound',
    path: '*',
    component: NotFound,
    options: { title: '404' },
    permissions: [],
  },
];

const linking: LinkingOptions<ReactNavigation.RootParamList> = {
  prefixes: ['app://'],
  config: {
    screens: {
      ...ROUTES.reduce((acc, route) => {
        acc[route.name] = {
          path: route.path,
        };

        return acc;
      }, {} as PathConfigMap<RootStackParamList>),
      Login: 'login',
      ForgotPassword: 'forgot-password',
      ResetPassword: 'reset-password',
    },
  },
};

const Stack = createSidebarNavigator();

const screensWithoutSidebar: (keyof RootStackParamList)[] = [
  'Login',
  'ForgotPassword',
  'ResetPassword',
];

export const Router = () => {
  const dispatch = useAppDispatch();

  const routeNameRef = useRef<string | undefined>();

  const triptych = useTriptych();

  const isAuthenticated = useAppSelector(selectIsAuthenticated);

  const checkPermissions = useCheckPermissions();

  const [isAppReady, setAppReady] = React.useState(false);

  useEffect(() => {
    (async () => {
      try {
        const accessToken = await AsyncStorage.getItem(ACCESS_TOKEN);
        const refreshToken = await AsyncStorage.getItem(REFRESH_TOKEN);

        dispatch(authSlice.actions.setInitialState({ accessToken, refreshToken }));
      } finally {
        setAppReady(true);
      }
    })();
  }, [dispatch]);

  if (!isAppReady) {
    return null;
  }

  return (
    <NavigationContainer
      linking={linking}
      ref={navigationRef}
      onReady={() => {
        routeNameRef.current = currentRoute();
      }}
      onStateChange={() => {
        const currentRouteName = currentRoute();
        const previousRouteName = routeNameRef.current;

        const trackScreenView = () => {
          triptych.reset();
        };

        if (previousRouteName !== currentRouteName) {
          routeNameRef.current = currentRouteName;

          trackScreenView();
        }
      }}
      documentTitle={{
        formatter: (options, route) => `Tick'eat Dashboard - ${options?.title ?? route?.name}`,
      }}>
      <ToasterContainer />

      <Stack.Navigator
        screensWithoutSidebar={screensWithoutSidebar}
        screenOptions={{ headerShown: false, animation: 'slide_from_left' }}
        initialRouteName="Login">
        {isAuthenticated ? (
          <Stack.Group navigationKey={isAuthenticated ? 'user' : 'guest'}>
            {ROUTES.map(
              ({ name, component, options, permissions }) =>
                (!permissions.length || checkPermissions(permissions)) && (
                  <Stack.Screen key={name} name={name} component={component} options={options} />
                ),
            )}
            <Stack.Screen
              name="ResetPassword"
              component={ResetPassword}
              options={{ title: 'R\u00e9initialiser le mot de passe' }}
            />
          </Stack.Group>
        ) : (
          <Stack.Group>
            <Stack.Screen name="Login" component={Login} options={{ title: 'Connexion' }} />
            <Stack.Screen
              name="ForgotPassword"
              component={ForgotPassword}
              options={{ title: 'Mot de passe oubli\u00e9' }}
            />
            <Stack.Screen
              name="ResetPassword"
              component={ResetPassword}
              options={{ title: 'R\u00e9initialiser le mot de passe' }}
            />
          </Stack.Group>
        )}
      </Stack.Navigator>
    </NavigationContainer>
  );
};
